import {
    Feed, FeedWithTokens, WidgetTheme
} from '@api';
import {
    Calculate, DynamicFeed, InfoOutlined, WarningAmber
} from '@mui/icons-material';
import {
    Button,
    DialogContent,
    FormControlLabel,
    InputAdornment,
    MenuItem,
    Paper,
    Switch,
    TextField,
    Tooltip,
    Typography
} from '@mui/material';
import { getAppConfig } from '@redux/config';
import { getFeedsList, useEntitySelector } from '@redux/entities';
import { useSelector } from '@redux/store';
import { CopyButton, Dialog, DialogProps } from '@tsp-ui/components';
import {
    useMemo, useState
} from 'react';
import { PrismAsyncLight as SyntaxHighlighter } from 'react-syntax-highlighter';
import prism from 'react-syntax-highlighter/dist/esm/styles/prism/material-light';

import { SelectableButton } from '../../../components/SelectableButton';

import styles from './EmbedWidgetDialog.module.scss';


interface EmbedWidgetDialogProps extends Omit<DialogProps, 'title'> {
    feed?: Feed;
    theme?: WidgetTheme;
    calculatorId?: string;
}

type Variant = 'feed-rates' | 'payment-calculator' | 'dti-calculator' | 'refinance-calculator';
const urlRegex = /^(https?):\/\/[^/:]+(:\d+)?/;

export default function EmbedWidgetDialog({
    open,
    feed: feedProp,
    theme,
    calculatorId,
    ...props
}: EmbedWidgetDialogProps) {
    const { widgetAssetUrls } = useSelector(getAppConfig) || {};

    const [ feedId, setFeedId ] = useState(feedProp?.id || '');
    const [ variant, setVariant ] = useState<Variant | undefined>(feedProp ? 'feed-rates' : undefined);
    const [ targetOrigin, setTargetOrigin ] = useState('');
    const [ originTouched, setOriginTouched ] = useState(false);
    const [ isLegacyEmbed, setIsLegacyEmbed ] = useState(false);

    const validatedTargetOrigin = urlRegex.test(targetOrigin) ? targetOrigin : '';
    const showOriginError = Boolean(!validatedTargetOrigin && targetOrigin && originTouched);

    const feeds = useEntitySelector({
        entityType: 'feeds',
        selector: getFeedsList,
        forceRefresh: true
    });

    const feed = feeds?.find((feed) => feed.id === feedId);
    const legacyCode = useEmbeddedCodeLegacy(feed, theme, calculatorId, variant);
    const newCode = useEmbeddedCode(feed, theme, calculatorId, variant, validatedTargetOrigin);

    const code = isLegacyEmbed ? legacyCode : newCode;

    return (
        <Dialog
            {...props}
            title="Embed widget"
            open={open}
            keepMounted
            maxWidth={false}
            loading={!widgetAssetUrls || props.loading}
        >
            <DialogContent>
                {!feedProp && (
                    <>
                        <Typography align="center">
                            Which type of widget would you like to embed?
                        </Typography>

                        <div className={styles.widgetTypeButtons}>
                            <SelectableButton
                                selected={variant === 'feed-rates'}
                                onClick={() => setVariant('feed-rates')}
                                className={styles.widgetTypeButton}
                            >
                                <DynamicFeed
                                    color="secondary"
                                    className={styles.icon}
                                />

                                Feed rate widget
                            </SelectableButton>

                            <SelectableButton
                                selected={variant === 'payment-calculator'}
                                onClick={() => {
                                    setVariant('payment-calculator');
                                    setFeedId('');
                                }}
                                className={styles.widgetTypeButton}
                            >
                                <Calculate
                                    color="secondary"
                                    className={styles.icon}
                                />

                                Payment calculator widget
                            </SelectableButton>

                            <SelectableButton
                                selected={variant === 'dti-calculator'}
                                onClick={() => {
                                    setVariant('dti-calculator');
                                    setFeedId('');
                                }}
                                className={styles.widgetTypeButton}
                            >
                                <Calculate
                                    color="secondary"
                                    className={styles.icon}
                                />

                                DTI calculator widget
                            </SelectableButton>

                            <SelectableButton
                                selected={variant === 'refinance-calculator'}
                                onClick={() => {
                                    setVariant('refinance-calculator');
                                    setFeedId('');
                                }}
                                className={styles.widgetTypeButton}
                            >
                                <Calculate
                                    color="secondary"
                                    className={styles.icon}
                                />

                                Refinance calculator widget
                            </SelectableButton>
                        </div>
                    </>
                )}

                {!feedProp && variant === 'feed-rates' && (
                    <>
                        <Typography align="center">
                            Which feed would you like to embed the rate widget for?
                        </Typography>

                        <div className={styles.inputContainer}>
                            <TextField
                                label="Feed to embed"
                                value={feedId}
                                onChange={(event) => setFeedId(event.target.value)}
                                select
                            >
                                {feeds?.map((feed => (
                                    <MenuItem
                                        key={feed.id}
                                        value={feed.id}
                                    >
                                        {feed.name}
                                    </MenuItem>
                                )))}
                            </TextField>
                        </div>
                    </>
                )}

                {(variant === 'payment-calculator'
                  || variant === 'dti-calculator'
                  || variant === 'refinance-calculator'
                  || (variant === 'feed-rates' && feedId)
                ) && (
                    <>
                        {!isLegacyEmbed && (
                            <>
                                <Typography align="center">
                                    What is the origin of the website where this widget will be embedded?
                                </Typography>

                                <div className={styles.inputContainer}>
                                    <TextField
                                        autoFocus
                                        label="Website origin"
                                        value={targetOrigin}
                                        onChange={(event) => setTargetOrigin(event.target.value)}
                                        placeholder="https://example.com"
                                        error={showOriginError}
                                        helperText={showOriginError
                                            ? 'Enter a valid origin (i.e. https://example.com)'
                                            : undefined}
                                        onBlur={() => setOriginTouched(true)}
                                        InputProps={{
                                            endAdornment: (
                                                <InputAdornment position="end">
                                                    <Tooltip
                                                        title="This is required to facilitate automatic height adjustment
                                                    of the embedded iframe based on the height of its contents"
                                                    >
                                                        <InfoOutlined color="primary" />
                                                    </Tooltip>
                                                </InputAdornment>
                                            )
                                        }}
                                    />
                                </div>
                            </>
                        )}

                        <div className={styles.useLegacyCheckboxContainer}>
                            <FormControlLabel
                                label="Use legacy embedding"
                                control={(
                                    <Switch
                                        name="useLegacyEmbed"
                                        checked={isLegacyEmbed}
                                        onChange={() => setIsLegacyEmbed(!isLegacyEmbed)}
                                    />
                                )}
                            />

                            <Tooltip
                                title="Legacy embedding will load your widgets into your webpage directly.
                                    We recommend using the default mechanism of embedding, which uses an iframe to
                                    embed the widgets."
                            >
                                <InfoOutlined color="primary" />
                            </Tooltip>
                        </div>

                        <Typography className={styles.instructions}>
                            Copy the following code and insert it into the HTML of your website

                            <div className={styles.instructionsIconContainer}>
                                {!isLegacyEmbed && !validatedTargetOrigin && (
                                    <Tooltip
                                        title="Automatic height adjustment not enabled. Enter the origin of your website
                                            above to enable automatic height adjustment for better layout control."
                                    >
                                        <WarningAmber color="warning" />
                                    </Tooltip>
                                )}

                                <CopyButton
                                    textToCopy={code}
                                    successMessage="Widget code copied"
                                    children={({ onClick }) => (
                                        <Button onClick={onClick}>
                                            Copy code
                                        </Button>
                                    )}
                                />
                            </div>
                        </Typography>

                        <Paper
                            variant="outlined"
                            className={styles.codePaper}
                        >
                            <SyntaxHighlighter
                                language="html"
                                style={prism}
                                wrapLongLines
                            >
                                {code}
                            </SyntaxHighlighter>
                        </Paper>
                    </>
                )}
            </DialogContent>
        </Dialog>
    );
}

function useEmbeddedCode(
    feed: FeedWithTokens | undefined,
    theme: WidgetTheme | undefined,
    calculatorId: string | undefined,
    variant: Variant | undefined,
    targetOrigin: string
) {
    const { widgetAssetUrls } = useSelector(getAppConfig) || {};

    const queryString = useMemo(() => {
        const params = new URLSearchParams();

        if (targetOrigin !== '') {
            params.set('targetOrigin', targetOrigin);
        }

        if (variant !== 'dti-calculator') {
            const id = calculatorId ?? feed?.id;

            if (id !== undefined) {
                params.set('id', variant === 'feed-rates' ? feed?.id ?? '' : id);
            }

            const token = feed?.feedTokens?.[0].token;
            if (variant === 'feed-rates' && token) {
                params.set('feedToken', token);
            }
        }

        if (theme) {
            params.set('primaryColor', theme.primaryColor);
            params.set('secondaryColor', theme.secondaryColor);
            params.set('calculatorBackgroundColor', theme.calculatorBackgroundColor);
            params.set('rateWidgetBackgroundColor', theme.rateWidgetBackgroundColor);
        }

        return params.toString();
    }, [
        targetOrigin, variant, theme, calculatorId, feed
    ]);

    const frameOrigin = widgetAssetUrls?.iframe;

    return useMemo(() => {
        const id = `ppm-${variant}-widget`;

        return (`<iframe
    id="${id}"
    src="${frameOrigin}/${variant}?${queryString}"
></iframe>${targetOrigin && `

<script>
    window.addEventListener('message', function(event) {
        const { height, widgetType } = event.data;

        if (event.origin === '${frameOrigin}' && height && widgetType) {
            const iframe = document.getElementById('ppm-' + widgetType + '-widget');
            iframe.style.height = height + 'px';
        }
    });
</script>`}`);
    }, [
        variant, frameOrigin, queryString, targetOrigin
    ]);
}

function useEmbeddedCodeLegacy(
    feed: FeedWithTokens | undefined,
    theme: WidgetTheme | undefined,
    calculatorId: string | undefined,
    variant: Variant | undefined
) {
    const { apiUrl, widgetAssetUrls } = useSelector(getAppConfig) || {};

    return useMemo(() => {
        if (!variant) {
            return '';
        }

        const id = `ppm-${variant}-widget`;

        const initScripts: Record<Variant, string> = {
            'feed-rates': `window.PPMWidgets.initRateWidget(document.getElementById('${id}'), {
            id: '${feed?.id}',
            feedToken: '${feed?.feedTokens?.[0].token}'
        });`,
            'payment-calculator': `window.PPMWidgets.initCalculator(document.getElementById('${id}'), {
            id: '${calculatorId}'
        });`,
            'dti-calculator': `window.PPMWidgets.initDTICalculator(document.getElementById('${id}'));`,
            'refinance-calculator': `window.PPMWidgets.initRefinanceCalculator(document.getElementById('${id}'), {
            id: '${calculatorId}'
        });`
        };

        return (`<div id="${id}"></div>
<script>
    const cssPromise = injectLink('${widgetAssetUrls?.css}');
    const fontPromise = injectLink('https://fonts.googleapis.com/css?family=Roboto:400,500&display=swap');
    
    function ppm_onWidgetLoad() {
        window.PPMWidgets.registerAssetsToAwait(cssPromise, fontPromise);
        
        window.PPMWidgets.configure({
            urlBase: '${apiUrl}/products/ppm',
            theme: ${!theme ? 'undefined' : `{
                primaryColor: '${theme.primaryColor}',
                secondaryColor: '${theme.secondaryColor}',
                calculatorBackgroundColor: '${theme.calculatorBackgroundColor}',
                rateWidgetBackgroundColor: '${theme.rateWidgetBackgroundColor}'
            }`} 
        });
        
        ${initScripts[variant]}
    }
    
    function injectLink(url) {
        const link = document.createElement('link');
        link.rel = 'stylesheet';
        link.href = url;
    
        const promise = new Promise((resolve) => {
            link.addEventListener('load', () => setTimeout(resolve, 50));
        });
        
        document.head.appendChild(link);
        
        return promise;
    }
</script>
<script
    async
    src="${widgetAssetUrls?.js}"
    onload="ppm_onWidgetLoad()"
></script>`);
    }, [
        apiUrl, widgetAssetUrls, feed, theme, calculatorId, variant
    ]);
}
